import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, MessageCircle, PhoneIncoming, CalendarCheck, Map } from "lucide-react";
import { Logo } from "../components/Logo";
import { SITE } from "../lib/site";

const NEXT_STEPS = [
  {
    icon: PhoneIncoming,
    title: "We call you within 24 hours",
    copy: "A senior strategist — not a salesperson — reviews your details and calls you.",
  },
  {
    icon: CalendarCheck,
    title: "Free 30-minute strategy call",
    copy: "We dig into your goals, current ads and market. You keep every insight, free.",
  },
  {
    icon: Map,
    title: "Custom growth roadmap in 48 hours",
    copy: "A clear plan with budgets, audiences and targets. Yours to use, with or without us.",
  },
];

export default function ThankYou() {
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "lead_generated" });
    if (typeof window.fbq === "function") window.fbq("track", "Lead");
    if (
      typeof window.gtag === "function" &&
      process.env.REACT_APP_GADS_SEND_TO &&
      !window.__gadsConversionFired
    ) {
      window.__gadsConversionFired = true;
      window.gtag("event", "conversion", {
        send_to: process.env.REACT_APP_GADS_SEND_TO,
        value: 1.0,
        currency: "INR",
      });
    }
  }, []);

  return (
    <div data-testid="thank-you-page" className="min-h-screen bg-[#050A1E] flex flex-col px-4 relative overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-blue-600/15 blur-[140px] pointer-events-none" />
      <header className="relative z-10 max-w-3xl w-full mx-auto pt-8">
        <Logo />
      </header>
      <main className="relative z-10 flex-1 flex items-center justify-center py-14">
        <div className="max-w-2xl w-full text-center space-y-8">
          <motion.div
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.1 }}
            className="mx-auto w-20 h-20 rounded-full gradient-bg flex items-center justify-center glow-blue"
            data-testid="success-check"
          >
            <Check size={38} className="text-white" strokeWidth={3} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-4"
          >
            <h1 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              We've Got Your <span className="gradient-accent-text">Details!</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-300 max-w-md mx-auto" data-testid="thank-you-message">
              We'll call you within <span className="text-white font-semibold">24 hours</span>.
              Keep your phone nearby — we're usually much faster.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
            {NEXT_STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                data-testid={`next-step-${i}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.5 + i * 0.15 }}
                className="glass-card rounded-2xl p-5"
              >
                <s.icon size={20} className="text-cyan-400 mb-3" />
                <h3 className="font-display font-semibold text-white text-sm">{s.title}</h3>
                <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{s.copy}</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 1 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <a
              data-testid="thank-you-whatsapp-button"
              href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(SITE.whatsappMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 bg-[#25D366] text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:brightness-110 hover:shadow-[0_0_30px_rgba(37,211,102,0.4)] transition-[box-shadow,filter] duration-300"
            >
              <MessageCircle size={18} className="fill-white" />
              Chat With Us On WhatsApp Now
            </a>
            <Link
              data-testid="back-home-link"
              to="/"
              className="text-sm font-medium text-slate-400 hover:text-white transition-colors duration-200"
            >
              ← Back to homepage
            </Link>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
